import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { CommentsPanel } from '../components/CommentsPanel.tsx'
import { SendToChatDialog } from '../components/SendToChatDialog.tsx'
import { mondayOf, monthOf } from '../lib/planner.ts'

/**
 * РЕЦЕПТЫ И МЕНЮ НА НЕДЕЛЮ
 *
 * Две вкладки, и вторая важнее первой. Рецепты сами по себе — книга,
 * какую можно найти где угодно; ценность появляется, когда из меню
 * на неделю одним нажатием собирается список покупок.
 *
 * Поэтому кнопка «в список покупок» стоит прямо в меню, а не спрятана
 * в подробностях рецепта.
 */

type Recipe = {
  id: string
  title: string
  description: string | null
  servings: number | null
  cookMinutes: number | null
  sourceUrl: string | null
  author: { id: string; displayName: string; avatarUrl: string | null } | null
  mine: boolean
  ingredientCount: number
}

type Ingredient = { name: string; quantity: number | null; unit: string | null }

type Meal = {
  id: string
  date: string
  mealType: string
  recipe: { id: string; title: string; cookMinutes: number | null } | null
  customTitle: string | null
  createdBy: { id: string; displayName: string } | null
}

const MEALS: { id: string; name: string }[] = [
  { id: 'BREAKFAST', name: 'Завтрак' },
  { id: 'LUNCH', name: 'Обед' },
  { id: 'DINNER', name: 'Ужин' },
  { id: 'SNACK', name: 'Перекус' },
]

const WEEKDAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье']

const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export function RecipesPage() {
  const [tab, setTab] = useState<'menu' | 'recipes'>('menu')
  const [recipes, setRecipes] = useState<Recipe[] | null>(null)
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState<'new' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ recipes: Recipe[] }>(`/api/recipes${query ? `?q=${encodeURIComponent(query)}` : ''}`)
      .then((data) => setRecipes(data.recipes))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [query])

  return (
    <div className="section">
      <header className="section-head">
        <h1>Еда</h1>
        {tab === 'recipes' && (
          <button className="icon-btn" onClick={() => setDraft('new')} aria-label="Новый рецепт">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
            </svg>
          </button>
        )}
      </header>

      <div className="planner-tabs">
        <button
          className={'tab-btn' + (tab === 'menu' ? ' is-active' : '')}
          onClick={() => setTab('menu')}
        >
          Меню на неделю
        </button>
        <button
          className={'tab-btn' + (tab === 'recipes' ? ' is-active' : '')}
          onClick={() => setTab('recipes')}
        >
          Рецепты{recipes && recipes.length > 0 && ` · ${recipes.length}`}
        </button>
      </div>

      {tab === 'menu' ? (
        <MenuView recipes={recipes ?? []} />
      ) : (
        <div className="section-body scroll">
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Найти по названию или составу — «кабачки»"
            maxLength={100}
          />

          {recipes?.length === 0 && (
            <p className="empty-hint">
              {query ? 'Ничего не нашлось.' : 'Рецептов пока нет. Запиши первый — хоть в двух строках.'}
            </p>
          )}

          <div className="card-grid" style={{ marginTop: 12 }}>
            {recipes?.map((recipe) => (
              <Link key={recipe.id} to={`/recipes/${recipe.id}`} className="list-card">
                <div className="list-card-top">
                  <span className="list-card-icon" aria-hidden="true">
                    🍲
                  </span>
                  <span className="list-card-name">{recipe.title}</span>
                </div>

                <div className="list-card-store">
                  {recipe.cookMinutes ? `${recipe.cookMinutes} мин` : 'время не указано'}
                  {recipe.servings ? ` · на ${recipe.servings}` : ''}
                </div>

                <div className="list-card-store">
                  {recipe.ingredientCount > 0
                    ? `ингредиентов: ${recipe.ingredientCount}`
                    : 'без состава'}
                </div>

                {recipe.author && !recipe.mine && (
                  <div className="list-card-owner">
                    <Avatar
                      id={recipe.author.id}
                      name={recipe.author.displayName}
                      url={recipe.author.avatarUrl}
                      size={20}
                    />
                    <span>{recipe.author.displayName}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>

          {error && <div className="sheet-error">{error}</div>}
        </div>
      )}

      {draft && (
        <RecipeSheet
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null)
            load()
          }}
        />
      )}
    </div>
  )
}

/** Неделя блюд и кнопка «собрать список покупок». */
function MenuView({ recipes }: { recipes: Recipe[] }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()))
  const [meals, setMeals] = useState<Meal[]>([])
  const [adding, setAdding] = useState<{ date: string; mealType: string } | null>(null)
  const [collecting, setCollecting] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const days = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(weekStart)
    day.setDate(weekStart.getDate() + index)
    return day
  })

  const from = isoDate(days[0]!)
  const to = isoDate(days[6]!)

  const load = () => {
    api<{ meals: Meal[] }>(`/api/recipes/menu/plan?from=${from}&to=${to}`)
      .then((data) => setMeals(data.meals))
      .catch(() => setMeals([]))
  }

  useEffect(load, [from, to])

  const remove = async (meal: Meal) => {
    setMeals((current) => current.filter((m) => m.id !== meal.id))
    await api(`/api/recipes/menu/plan/${meal.id}`, { method: 'DELETE' }).catch(load)
  }

  const mealsOf = (day: Date, mealType: string) =>
    meals.filter((m) => m.date.slice(0, 10) === isoDate(day) && m.mealType === mealType)

  const withRecipes = meals.filter((m) => m.recipe !== null).length

  return (
    <div className="section-body scroll">
      <div className="week-head">
        <button
          className="icon-btn"
          onClick={() => {
            const previous = new Date(weekStart)
            previous.setDate(weekStart.getDate() - 7)
            setWeekStart(previous)
          }}
          aria-label="Предыдущая неделя"
        >
          ‹
        </button>

        <span className="week-title">
          {days[0]!.getDate()}—{days[6]!.getDate()} {monthOf(days[6]!)}
        </span>

        <button
          className="icon-btn"
          onClick={() => {
            const next = new Date(weekStart)
            next.setDate(weekStart.getDate() + 7)
            setWeekStart(next)
          }}
          aria-label="Следующая неделя"
        >
          ›
        </button>

        <button className="link-btn" onClick={() => setWeekStart(mondayOf(new Date()))}>
          Эта неделя
        </button>
      </div>

      {days.map((day, index) => (
        <div key={isoDate(day)} className="menu-day">
          <div className="day-head">
            {WEEKDAYS[index]}, {day.getDate()} {monthOf(day)}
          </div>

          {MEALS.map((meal) => {
            const planned = mealsOf(day, meal.id)
            if (planned.length === 0 && meal.id === 'SNACK') return null

            return (
              <div key={meal.id} className="menu-slot">
                <span className="menu-slot-name">{meal.name}</span>

                <span className="menu-slot-items">
                  {planned.map((item) => (
                    <span key={item.id} className="menu-dish">
                      {item.recipe ? (
                        <Link to={`/recipes/${item.recipe.id}`}>{item.recipe.title}</Link>
                      ) : (
                        <span>{item.customTitle}</span>
                      )}
                      <button
                        className="menu-dish-remove"
                        onClick={() => void remove(item)}
                        aria-label="Убрать"
                      >
                        ✕
                      </button>
                    </span>
                  ))}

                  <button
                    className="link-btn"
                    onClick={() => setAdding({ date: isoDate(day), mealType: meal.id })}
                  >
                    +
                  </button>
                </span>
              </div>
            )
          })}
        </div>
      ))}

      {withRecipes > 0 && (
        <>
          <button className="btn-primary" onClick={() => setCollecting(true)}>
            Собрать список покупок
          </button>
          <p className="sheet-note">
            Всё, что нужно для блюд этой недели, уйдёт в выбранный список. Одинаковое
            сложится, уже записанное не задвоится.
          </p>
        </>
      )}

      {note && <div className="sheet-ok">{note}</div>}

      {adding && (
        <AddDishSheet
          date={adding.date}
          mealType={adding.mealType}
          recipes={recipes}
          onClose={() => setAdding(null)}
          onSaved={load}
        />
      )}

      {collecting && (
        <CollectSheet
          from={from}
          to={to}
          onClose={() => setCollecting(false)}
          onDone={(text) => {
            setNote(text)
            setCollecting(false)
          }}
        />
      )}
    </div>
  )
}

function AddDishSheet({
  date,
  mealType,
  recipes,
  onClose,
  onSaved,
}: {
  date: string
  mealType: string
  recipes: Recipe[]
  onClose: () => void
  onSaved: () => void
}) {
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async (recipeId: string | null) => {
    setBusy(true)
    try {
      await api('/api/recipes/menu/plan', {
        method: 'POST',
        body: {
          date,
          mealType,
          recipeId,
          customTitle: recipeId ? null : custom.trim(),
        },
      })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>Что готовим</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <div className="add-row" style={{ border: 'none', padding: 0 }}>
          <input
            className="input"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && custom.trim() && void add(null)}
            placeholder="Просто название — «пельмени»"
            maxLength={200}
            autoFocus
          />
          <button
            className="btn-primary"
            onClick={() => void add(null)}
            disabled={busy || !custom.trim()}
          >
            Добавить
          </button>
        </div>

        {recipes.length > 0 && (
          <>
            <h3 className="sheet-sub">Или из рецептов</h3>
            <div className="member-list">
              {recipes.map((recipe) => (
                <button
                  key={recipe.id}
                  className="member-row pick"
                  onClick={() => void add(recipe.id)}
                  disabled={busy}
                >
                  <span className="circle-badge" aria-hidden="true">
                    🍲
                  </span>
                  <div className="member-text">
                    <div className="member-name">{recipe.title}</div>
                    <div className="member-sub">
                      {recipe.ingredientCount > 0
                        ? `ингредиентов: ${recipe.ingredientCount}`
                        : 'без состава'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function CollectSheet({
  from,
  to,
  onClose,
  onDone,
}: {
  from: string
  to: string
  onClose: () => void
  onDone: (note: string) => void
}) {
  const [lists, setLists] = useState<{ id: string; name: string; mine: boolean }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api<{ lists: { id: string; name: string; mine: boolean }[] }>('/api/shopping')
      .then((data) => setLists(data.lists))
      .catch(() => setLists([]))
  }, [])

  const collect = async (listId: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ added: number; skipped: string[] }>(
        '/api/recipes/menu/to-shopping',
        { method: 'POST', body: { from, to, listId } },
      )

      onDone(
        result.added === 0
          ? 'Всё нужное уже есть в списке'
          : `Добавлено в список: ${result.added}` +
              (result.skipped.length ? `, уже было: ${result.skipped.length}` : ''),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>В какой список</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {lists.length === 0 && (
          <p className="sheet-text">
            Списков покупок пока нет — заведи его в разделе «Покупки».
          </p>
        )}

        <div className="member-list">
          {lists.map((list) => (
            <button
              key={list.id}
              className="member-row pick"
              onClick={() => void collect(list.id)}
              disabled={busy}
            >
              <span className="circle-badge" aria-hidden="true">
                🛒
              </span>
              <div className="member-text">
                <div className="member-name">{list.name}</div>
                {!list.mine && <div className="member-sub">общий список</div>}
              </div>
            </button>
          ))}
        </div>

        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}

/* Один рецепт -------------------------------------------------------------- */

export function RecipePage() {
  const { recipeId } = useParams<{ recipeId: string }>()
  const [recipe, setRecipe] = useState<
    (Recipe & { ingredients: (Ingredient & { id: string })[]; steps: string[] }) | null
  >(null)
  const [editing, setEditing] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    if (!recipeId) return
    api<{ recipe: Recipe & { ingredients: (Ingredient & { id: string })[]; steps: string[] } }>(
      `/api/recipes/${recipeId}`,
    )
      .then((data) => setRecipe(data.recipe))
      .catch((e) => setError(e instanceof Error ? e.message : 'Рецепт не найден'))
  }

  useEffect(load, [recipeId])

  if (error) {
    return (
      <div className="section">
        <div className="center-screen">
          <p className="empty-hint">{error}</p>
        </div>
      </div>
    )
  }

  if (!recipe) {
    return (
      <div className="section">
        <div className="center-screen">
          <div className="spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="section">
      <header className="section-head">
        <Link to="/recipes" className="icon-btn" aria-label="Назад">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
          </svg>
        </Link>

        <div className="section-title">
          <h1>{recipe.title}</h1>
          <span className="section-sub">
            {recipe.cookMinutes ? `${recipe.cookMinutes} мин` : ''}
            {recipe.servings ? ` · на ${recipe.servings}` : ''}
          </span>
        </div>

        <button className="icon-btn" onClick={() => setSending(true)} title="Отправить в переписку">
          <svg viewBox="0 0 24 24" width="21" height="21" fill="currentColor">
            <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
          </svg>
        </button>

        {recipe.mine && (
          <button className="icon-btn" onClick={() => setEditing(true)} aria-label="Изменить">
            ✎
          </button>
        )}
      </header>

      <div className="section-body scroll">
        {recipe.description && <p className="record-summary">{recipe.description}</p>}

        {recipe.ingredients.length > 0 && (
          <>
            <h3 className="sheet-sub">Что нужно</h3>
            <div className="ingredient-list">
              {recipe.ingredients.map((ingredient) => (
                <div key={ingredient.id} className="ingredient-row">
                  <span>{ingredient.name}</span>
                  <span className="ingredient-amount">
                    {ingredient.quantity !== null
                      ? `${ingredient.quantity.toLocaleString('ru-RU')} ${ingredient.unit ?? ''}`
                      : ingredient.unit ?? ''}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {recipe.steps.length > 0 && (
          <>
            <h3 className="sheet-sub">Как готовить</h3>
            <ol className="steps-list">
              {recipe.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </>
        )}

        {recipe.sourceUrl && (
          <p className="sheet-text">
            <a className="auth-link" href={recipe.sourceUrl} target="_blank" rel="noreferrer">
              Откуда рецепт
            </a>
          </p>
        )}

        <h3 className="sheet-sub">Обсуждение</h3>
        <CommentsPanel entityType="RECIPE" entityId={recipe.id} />
      </div>

      {editing && (
        <RecipeSheet
          existing={recipe}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            load()
          }}
        />
      )}

      {sending && (
        <SendToChatDialog
          entityType="RECIPE"
          entityId={recipe.id}
          title={recipe.title}
          onClose={() => setSending(false)}
        />
      )}
    </div>
  )
}

/**
 * Правка рецепта.
 *
 * Состав и шаги набираются построчно в обычных полях: строка — один
 * ингредиент. Так проще всего перенести рецепт из книги или с сайта,
 * а именно этим и занимаются в первый раз.
 */
function RecipeSheet({
  existing,
  onClose,
  onSaved,
}: {
  existing?: Recipe & { ingredients: Ingredient[]; steps: string[] }
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(existing?.title ?? '')
  const [servings, setServings] = useState(existing?.servings?.toString() ?? '')
  const [cookMinutes, setCookMinutes] = useState(existing?.cookMinutes?.toString() ?? '')
  const [sourceUrl, setSourceUrl] = useState(existing?.sourceUrl ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')

  const [ingredients, setIngredients] = useState(() =>
    (existing?.ingredients ?? [])
      .map((i) =>
        [i.name, i.quantity !== null ? String(i.quantity) : '', i.unit ?? '']
          .filter(Boolean)
          .join(' — '),
      )
      .join('\n'),
  )
  const [steps, setSteps] = useState((existing?.steps ?? []).join('\n'))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** «Свёкла — 2 — шт» или «Свёкла 2 шт» — разбираем и так, и так. */
  const parseIngredients = () =>
    ingredients
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split('—').map((p) => p.trim())
        if (parts.length >= 2) {
          const quantity = Number((parts[1] ?? '').replace(',', '.'))
          return {
            name: parts[0] ?? line,
            quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null,
            unit: parts[2] || null,
          }
        }

        // Без тире: «Свёкла 2 шт» — число и единица в конце строки
        const match = /^(.*?)\s+([\d.,]+)\s*([^\d\s]*)$/.exec(line)
        if (match) {
          const quantity = Number((match[2] ?? '').replace(',', '.'))
          return {
            name: (match[1] ?? line).trim(),
            quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null,
            unit: match[3] || null,
          }
        }

        return { name: line, quantity: null, unit: null }
      })

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        servings: servings.trim() ? Number(servings) : null,
        cookMinutes: cookMinutes.trim() ? Number(cookMinutes) : null,
        sourceUrl: sourceUrl.trim() || null,
        ingredients: parseIngredients(),
        steps: steps
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      }

      if (existing) {
        await api(`/api/recipes/${existing.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/recipes', { method: 'POST', body })
      }

      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!existing || !window.confirm('Удалить рецепт?')) return
    setBusy(true)
    try {
      await api(`/api/recipes/${existing.id}`, { method: 'DELETE' })
      window.location.href = '/recipes'
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{existing ? 'Правка рецепта' : 'Новый рецепт'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Название</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Борщ"
            maxLength={200}
            autoFocus={!existing}
          />
        </label>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Порций</span>
            <input
              className="input"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
              placeholder="6"
              inputMode="numeric"
            />
          </label>
          <label className="field">
            <span className="field-label">Минут</span>
            <input
              className="input"
              value={cookMinutes}
              onChange={(e) => setCookMinutes(e.target.value)}
              placeholder="120"
              inputMode="numeric"
            />
          </label>
        </div>

        <label className="field">
          <span className="field-label">Состав — по одному в строке</span>
          <textarea
            className="input"
            rows={6}
            value={ingredients}
            onChange={(e) => setIngredients(e.target.value)}
            placeholder={'Свёкла — 2 — шт\nКапуста — 300 — г\nСметана'}
          />
          <span className="field-hint">
            Можно и просто «Свёкла 2 шт» — разберём. Количество нужно, чтобы
            собрать список покупок.
          </span>
        </label>

        <label className="field">
          <span className="field-label">Шаги — по одному в строке</span>
          <textarea
            className="input"
            rows={5}
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            placeholder={'Сварить бульон\nНашинковать овощи'}
          />
        </label>

        <label className="field">
          <span className="field-label">Ссылка на источник</span>
          <input
            className="input"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="Не обязательно"
            maxLength={1000}
          />
        </label>

        <label className="field">
          <span className="field-label">Заметка</span>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Чем этот рецепт лучше других"
            maxLength={2000}
          />
        </label>

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {existing?.mine && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Удалить
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
